import { createReducer, on } from "@ngrx/store";
import { HttpErrorResponse } from "@angular/common/http";
import {
  loadPackageListFailure,
  packageDeleteSubgroup,
  packageListLoad,
  packageListLoadSuccess,
  packageUpdateSubgroup,
} from "../../actions/packages/packages.actions";
import { Package } from "src/app/shared/interfaces/packages/package";
import { createEntityAdapter, EntityState } from "@ngrx/entity";

export interface PackageListState extends EntityState<Package> {
  error: HttpErrorResponse | null;
}

export const packagesAdapter = createEntityAdapter<Package>();
const initialState: PackageListState = packagesAdapter.getInitialState({
  error: null,
});

export const packageListReducer = createReducer(
  initialState,
  on(packageListLoad, (_state): PackageListState => initialState),
  on(
    packageListLoadSuccess,
    (state, action): PackageListState =>
      packagesAdapter.upsertMany(action.packages, { ...state, error: null }),
  ),
  on(packageUpdateSubgroup, (state, action): PackageListState => {
    let pkg = state.entities[action.packageId];
    if (!pkg) return state;

    let packageLimit = 0;

    const courses = pkg.courses.map((sub) => {
      if (sub.course.id !== action.courseId) {
        packageLimit += sub.limit;
        return sub;
      }

      const existingSubgroup = sub.subgroups.find(
        (sg) => sg.id === action.subgroup.id,
      );

      const teamLimitOffset = existingSubgroup
        ? action.subgroup.limit - existingSubgroup.limit
        : action.subgroup.limit;

      const teamUsedLimitOffset = existingSubgroup
        ? action.subgroup.used_limit - existingSubgroup.used_limit
        : action.subgroup.used_limit;

      packageLimit += sub.limit + teamLimitOffset;

      const updatedSubgroups = existingSubgroup
        ? sub.subgroups.map((sg) =>
            sg.id === action.subgroup.id ? action.subgroup : sg,
          )
        : [...sub.subgroups, action.subgroup];

      return {
        ...sub,
        subgroups: updatedSubgroups,
        used_limit: sub.used_limit + teamUsedLimitOffset,
        limit: sub.limit + teamLimitOffset,
      };
    });

    return packagesAdapter.upsertOne(
      {
        ...pkg,
        courses,
        used_limit: packageLimit,
      },
      state,
    );
  }),
  on(loadPackageListFailure, (state, { error }) => ({
    ...state,
    error: error,
  })),
  on(packageDeleteSubgroup, (state, action) => {
    const pkg = state.entities[action.packageId];
    if (!pkg) return state;

    let packageLimitOffset = 0;
    const courses = pkg.courses.map((sub) => {
      const sg = sub.subgroups.find((sg) => sg.id === action.subgroupId);
      if (!sg) return sub;

      packageLimitOffset += sg?.limit;
      const updatedSubgroups = sub.subgroups.filter(
        (sg) => sg.id !== action.subgroupId,
      );
      return {
        ...sub,
        subgroups: updatedSubgroups,
        used_limit: sub.used_limit - sg.used_limit,
        limit: sub.limit - sg.limit,
      };
    });

    return packagesAdapter.upsertOne(
      {
        ...pkg,
        courses,
        used_limit: pkg.used_limit - packageLimitOffset,
      },
      state,
    );
  }),
);
